import { useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { useVentures } from '../hooks/useVentures';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function VentureDetail() {
  const { id } = useParams();
  const { ventures, loading } = useVentures();
  const videoRef = useRef(null);
  const venture = ventures.find((v) => v.id === id);

  const handleVideoReady = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cv-bg)]">
        <div className="preloader-spinner" data-testid="venture-loading" />
      </div>
    );
  }

  if (!venture) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--cv-bg)] gap-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Venture not found</h1>
        <Link to="/" className="nav-link" data-testid="back-home-link">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cv-bg)]" data-testid="venture-detail-page">
      {/* Hero video */}
      <div className="venture-detail-hero">
        {venture.video && (
          <video
            ref={videoRef}
            className="venture-detail-video"
            autoPlay muted loop playsInline preload="metadata"
            poster={venture.img}
            onCanPlay={handleVideoReady}
            data-testid="venture-detail-video"
          >
            <source src={venture.video} type="video/mp4" />
          </video>
        )}
        {!venture.video && venture.img && (
          <img src={venture.img} alt={venture.title} className="venture-detail-video" />
        )}
        <div className="venture-detail-hero-overlay" />
        <div className="venture-detail-hero-content">
          <Link to="/" className="venture-back-link" data-testid="venture-back-link">
            <ArrowLeft size={16} />
            <span>Back</span>
          </Link>
          <motion.div {...fadeUp}>
            <p className="section-number mb-4">{venture.tag} &mdash; {venture.year}</p>
            <h1 className="venture-detail-title" data-testid="venture-detail-title">{venture.title}</h1>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {venture.description && (
          <motion.p
            className="venture-detail-desc"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="venture-detail-description"
          >
            {venture.description}
          </motion.p>
        )}

        <div className="venture-detail-grid">
          {venture.challenge && (
            <motion.div
              className="venture-detail-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7 }}
              data-testid="venture-detail-challenge"
            >
              <h3 className="venture-detail-block-title">The Challenge</h3>
              <p>{venture.challenge}</p>
            </motion.div>
          )}
          {venture.approach && (
            <motion.div
              className="venture-detail-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.7 }}
              data-testid="venture-detail-approach"
            >
              <h3 className="venture-detail-block-title">Our Approach</h3>
              <p>{venture.approach}</p>
            </motion.div>
          )}
          {venture.outcome && (
            <motion.div
              className="venture-detail-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.7 }}
              data-testid="venture-detail-outcome"
            >
              <h3 className="venture-detail-block-title">The Outcome</h3>
              <p>{venture.outcome}</p>
            </motion.div>
          )}
        </div>

        <motion.div
          className="mt-16 pt-8 border-t border-[var(--cv-border)]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/#ventures" className="inline-flex items-center gap-2 nav-link text-sm" data-testid="view-all-ventures-link">
            View all ventures <ArrowUpRight size={14} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
